
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { User } from "@/types";
import { toast } from "@/hooks/use-toast";

export interface HomeUser extends User {
  homeCourseId: string | null;
}

export const useHomeUser = () => {
  const [currentUser, setCurrentUser] = useState<HomeUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
          setCurrentUser(null);
          return;
        }

        const { data: profileData } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (profileData) {
          setCurrentUser({
            id: profileData.id,
            name: profileData.name || "User",
            email: profileData.email || "",
            imageUrl: profileData.image_url,
            homeCourseId: profileData.home_course_id,
          });
        } else {
          // No profile row yet — build from auth metadata
          setCurrentUser({
            id: user.id,
            name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
            email: user.email || '',
            imageUrl: user.user_metadata?.avatar_url || null,
            homeCourseId: null,
          });
        }
      } catch (error) {
        console.error("useHomeUser - Error fetching user:", error);
        toast({
          title: "Error fetching profile",
          description: "There was an error loading your profile data.",
          variant: "destructive",
        });
        setCurrentUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCurrentUser();
  }, []);

  return {
    currentUser,
    isLoadingUser: isLoading
  };
};
